import { ReactNode, useCallback, useEffect } from 'react'
import { styled, Typography } from '@mui/material'
import {
  FormatListBulletedIcon,
  LockIcon,
  QuestionOutlinedIcon,
  RecordIllustrationIcon,
  UploadIcon,
  WDiamondIcon,
} from '@weblite/icons'
import { asyncDelay } from '@weblite/logic-toolkit'
import {
  Devices,
  NotificationsActive as NotificationsActiveIcon,
  StarBorder,
  VisibilityOffOutlined,
} from '@mui/icons-material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { LiveIcon } from '$common/icons'
import { BottomDrawer } from '$common/bottom-drawer'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { SecondaryButton, SecondaryOutlinedButton } from '$common/buttons'
import { t } from '$i18n'
import { showEnablePremiumFeaturesTrialConfirmModal } from '$comp/modals/confirmation-dialog'
import { useAsyncFunction } from '$helper/functions/async-function.react-hook'
import { getTrialEndTime, TRIAL_PERIOD } from '$logic/premium-features/service'
import { showYourTrialHasExpiredSnackbar } from '$comp/snackbars'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined'
import { openUsageDashboard } from '$logic/wapps/business'
import { useIsAnyPlanEnabled } from '$logic/premium-features/react-hooks'
const ContentsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  padding: 32px 20px 28px;
  flex-grow: 1;

  &.isDesktop {
    padding-bottom: 16px;
  }
`

const IconContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.primary50};

  & > svg {
    width: 64px;
    height: 64px;
    color: ${({ theme }) => theme.palette.primary500};
  }
`

const ActionsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 20px;
`

export enum PremiumFeatureType {
  COURSE_VITRINE = 'CourseVitrine',
  CLASSROOM = 'Classroom',
  QUESTION_AND_ANSWER = 'QuestionAndAnswer',
  PROTECT_CONTENT = 'ProtectContent',
  STORY = 'Story',
  USER_STORY = 'UserStory',
  VOD_ACTIVATED_TAG = 'VodActivated',
  HIDE_MEMBER_COUNT = 'HideMemberCount',
  ALL = 'All',
  USER_SESSIONS_LIMIT = 'UserSessionsLimit',
  RECORDING = 'Recording',
  VOD = 'Vod',
  WORKSPACE = 'Workspace',
}

const FeatureTypeToIcon: Record<PremiumFeatureType, ReactNode> = {
  [PremiumFeatureType.COURSE_VITRINE]: <FormatListBulletedIcon />,
  [PremiumFeatureType.QUESTION_AND_ANSWER]: <QuestionOutlinedIcon />,
  [PremiumFeatureType.CLASSROOM]: <LiveIcon />,
  [PremiumFeatureType.PROTECT_CONTENT]: <LockIcon />,
  [PremiumFeatureType.STORY]: <NotificationsActiveIcon />,
  [PremiumFeatureType.USER_STORY]: <NotificationsActiveIcon />,
  [PremiumFeatureType.VOD_ACTIVATED_TAG]: <UploadIcon />,
  [PremiumFeatureType.HIDE_MEMBER_COUNT]: <VisibilityOffOutlined />,
  [PremiumFeatureType.ALL]: <WDiamondIcon />,
  [PremiumFeatureType.USER_SESSIONS_LIMIT]: <Devices />,
  [PremiumFeatureType.RECORDING]: <RecordIllustrationIcon />,
  [PremiumFeatureType.VOD]: <CloudOutlinedIcon />,
  [PremiumFeatureType.WORKSPACE]: <StarBorder />,
}

export interface BuyPremiumFeatureDialogProps {
  featureType: PremiumFeatureType
  isOpen?: boolean
  hallId?: string
}

export function BuyPremiumFeatureDialog({
  isOpen,
  featureType,
  hallId,
}: BuyPremiumFeatureDialogProps) {
  const isDesktopSize = useIsDesktopSize()
  const icon = FeatureTypeToIcon[featureType] ?? null

  const { value: lastTrialEndTime, loading } = useAsyncFunction(
    getTrialEndTime,
    [],
  )

  const isAnyPlanEnabled = useIsAnyPlanEnabled()

  useEffect(() => {
    if (lastTrialEndTime && lastTrialEndTime <= dateWithOffset()) {
      asyncDelay(500).then(() => {
        showYourTrialHasExpiredSnackbar()
      })
    }
  }, [lastTrialEndTime])

  const name = t(`premiumFeatureModal.features.${featureType}.title`)
  const description = t(
    `premiumFeatureModal.features.${featureType}.description`,
  )

  const enableDemo = useCallback(async () => {
    await closeCurrentModal()
    void showEnablePremiumFeaturesTrialConfirmModal()
    analite('SHOW_ENABLE_TRIAL_DEMO_CONFIRM_MODAL')
  }, [hallId])

  return (
    <BottomDrawer isOpen={isOpen!} onClose={closeCurrentModal}>
      <ContentsContainer className={isDesktopSize ? 'isDesktop' : ''}>
        <IconContainer>{icon}</IconContainer>

        <Typography
          dir="auto"
          variant="h2"
          marginTop="20px"
          sx={{ textAlign: 'center', color: 'neutral1000' }}
        >
          {name}
        </Typography>

        <Typography
          variant="paragraph1"
          sx={{ mt: 2, textAlign: 'center', color: 'neutral900' }}
        >
          {description}
        </Typography>
      </ContentsContainer>

      <ActionsContainer>
        <SecondaryButton
          fullWidth
          text={t('premiumFeatureModal.buy')}
          onClick={async () => {
            await closeCurrentModal()
            analite('OPEN_PREMIUM_FEATURE_INTRODUCTION', {
              source: 'PREMIUM_FEATURE_DIALOG',
            })
            openUsageDashboard()
          }}
        />

        {!isAnyPlanEnabled && (
          <SecondaryOutlinedButton
            fullWidth
            text={t('premiumFeatureModal.testFreeForNDays')(TRIAL_PERIOD)}
            onClick={enableDemo}
            disabled={loading || !!lastTrialEndTime}
          />
        )}
      </ActionsContainer>
    </BottomDrawer>
  )
}
